import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { createPortal } from 'react-dom';
import { X, Save, User, Phone, Mail, MapPin, Hash, CreditCard, Loader2 } from 'lucide-react';
import { Owner, Pet } from '../../types';
import { supabase } from '../../services/mockData';
import toast from 'react-hot-toast';
import FormField from '../Form/FormField';
import { useFormValidation } from '../../hooks/useFormValidation';
import { compose, required, email as emailValidator, phone as phoneValidator, minLength } from '../../utils/validators';

interface OwnerFormProps {
  owner?: Owner | null;
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (owner: Omit<Owner, 'id' | 'createdAt'>, petId?: string) => void;
}

const OwnerForm: React.FC<OwnerFormProps> = ({ owner, isOpen, onClose, onSubmit }) => {
  const [unownedPets, setUnownedPets] = useState<Pet[]>([]);
  const [selectedPetId, setSelectedPetId] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);

  const form = useFormValidation(
    {
      name: '',
      phone: '',
      email: '',
      address: '',
      chipNumber: '',
      passportNumber: '',
    },
    {
      name: compose(
        (v) => required(v, 'Ad soyad'),
        minLength(2)
      ),
      phone: compose(
        (v) => required(v, 'Telefon'),
        phoneValidator
      ),
      email: compose(
        (v) => required(v, 'E-posta'),
        emailValidator
      ),
      address: (v) => required(v, 'Adres'),
    }
  );

  // Owner yüklendiğinde formu doldur
  useEffect(() => {
    if (owner) {
      form.setValues({
        name: owner.name || '',
        phone: owner.phone || '',
        email: owner.email || '',
        address: owner.address || '',
        chipNumber: owner.chipNumber || '',
        passportNumber: owner.passportNumber || '',
      });
    } else if (isOpen) {
      form.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [owner, isOpen]);

  // Sahipsiz hayvanları çek
  useEffect(() => {
    if (!isOpen || owner) return;
    (async () => {
      try {
        const { data } = await supabase
          .from('pets')
          .select('*')
          .is('owner_id', null);
        setUnownedPets(data || []);
      } catch {
        setUnownedPets([]);
      }
    })();
  }, [isOpen, owner]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.validateAll()) {
      toast.error('Lütfen formdaki hataları düzeltin');
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit({ ...form.values, name: form.values.name.trim() }, selectedPetId);
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return typeof document !== 'undefined' ? createPortal(
    <div className="fixed inset-0 flex items-center justify-center z-[9999] p-4" style={{backgroundColor:"rgba(0,0,0,0.75)"}}>
      <motion.div
        initial={{ opacity: 0, scale: 0.92, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9 }}
        transition={{ type: 'spring', stiffness: 200, damping: 22 }}
        className="dribbble-panel max-w-md w-full max-h-[90vh] flex flex-col"
      >
        {/* Header (sabit) */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 shrink-0 bg-white rounded-t-2xl">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center text-white shadow-md shadow-teal-300/40">
              <User className="w-4 h-4" />
            </div>
            <h2 className="text-lg font-bold text-gray-900">
              {owner ? 'Sahip Düzenle' : 'Yeni Sahip Ekle'}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
          {/* Scroll alanı */}
          <div className="flex-1 overflow-y-auto p-6 space-y-3">
          <FormField
            label="Ad Soyad"
            name="name"
            icon={User}
            value={form.values.name}
            onChange={(v) => form.setValue('name', v)}
            onBlur={() => form.setFieldTouched('name')}
            error={form.errors.name}
            touched={form.touched.name}
            valid={!form.errors.name}
            placeholder="Ahmet Yılmaz"
            required
          />

          <FormField
            label="Telefon"
            name="phone"
            type="tel"
            icon={Phone}
            value={form.values.phone}
            onChange={(v) => form.setValue('phone', v)}
            onBlur={() => form.setFieldTouched('phone')}
            error={form.errors.phone}
            touched={form.touched.phone}
            valid={!form.errors.phone}
            placeholder="0532 123 45 67"
            hint="Klinikle iletişim için"
            required
          />

          <FormField
            label="E-posta"
            name="email"
            type="email"
            icon={Mail}
            value={form.values.email}
            onChange={(v) => form.setValue('email', v)}
            onBlur={() => form.setFieldTouched('email')}
            error={form.errors.email}
            touched={form.touched.email}
            valid={!form.errors.email}
            placeholder="ahmet@email.com"
            hint="Hasta sahibi mobil uygulamaya bu e-posta adresiyle giriş yapar. Doğru yazdığınızdan emin olun."
            required
          />

          <FormField
            label="Adres"
            name="address"
            icon={MapPin}
            value={form.values.address}
            onChange={(v) => form.setValue('address', v)}
            onBlur={() => form.setFieldTouched('address')}
            error={form.errors.address}
            touched={form.touched.address}
            valid={!form.errors.address}
            placeholder="Kadıköy, İstanbul"
            multiline
            rows={2}
            required
          />

          <FormField
            label="Çip Numarası"
            name="chipNumber"
            icon={Hash}
            value={form.values.chipNumber}
            onChange={(v) => form.setValue('chipNumber', v)}
            placeholder="TR123456789012345"
            hint="Opsiyonel"
          />

          <FormField
            label="Pasaport Numarası"
            name="passportNumber"
            icon={CreditCard}
            value={form.values.passportNumber}
            onChange={(v) => form.setValue('passportNumber', v)}
            placeholder="A12345678"
            hint="Opsiyonel"
          />

          {!owner && unownedPets.length > 0 && (
            <div className="space-y-1.5">
              <label className="block text-sm font-semibold text-gray-700">
                Hayvan Ata <span className="text-gray-400 font-normal">(Opsiyonel)</span>
              </label>
              <select
                value={selectedPetId}
                onChange={(e) => setSelectedPetId(e.target.value)}
                className="w-full rounded-xl border-2 border-gray-200 focus:border-teal-400 focus:ring-4 focus:ring-teal-500/20 outline-none px-3 py-2.5 text-sm bg-white"
              >
                <option value="">Sahipsiz bir hayvan seç...</option>
                {unownedPets.map((pet) => (
                  <option key={pet.id} value={pet.id}>
                    {pet.name} ({pet.breed})
                  </option>
                ))}
              </select>
            </div>
          )}

          </div>

          {/* Footer butonlar (sabit) */}
          <div className="flex gap-3 p-4 border-t border-gray-200 bg-white shrink-0 rounded-b-2xl">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-secondary flex-1"
              disabled={submitting}
            >
              İptal
            </button>
            <button
              type="submit"
              disabled={submitting || !form.isValid}
              className="btn btn-primary flex-1 flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Kaydediliyor...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  {owner ? 'Güncelle' : 'Kaydet'}
                </>
              )}
            </button>
          </div>
        </form>
      </motion.div>
    </div>,
    document.body
  ) : null;
};

export default OwnerForm;
